import * as yup from 'yup'

import { get as lodashGet, filter as lodashFilter, isEmpty as lodashIsEmpty } from 'lodash'
import { KnowledgeBaseContentType } from 'types/KnowledgeBaseContentType'
import { KnowledgeBaseVisibilityType } from 'types/KnowledgeBaseVisibilityType'
import { ImageSchema } from './ImageSchema'

declare module 'yup' {
  interface ArraySchema<T> {
    uniqueProperty(propertyPath: string, message?: string): ArraySchema<T>;
  }
}

yup.addMethod(yup.array, 'uniqueProperty', function uniquePropertyTest(propertyPath, message) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return this.test('unique', '', function uniqueTest(list: any) {
    const errors: yup.ValidationError[] = []

    list.forEach((item: unknown, index: number) => {
      const propertyValue = lodashGet(item, propertyPath)

      if (
        propertyValue
        && lodashFilter(list, [propertyPath, propertyValue]).length > 1
      ) {
        errors.push(
          this.createError({
            path: `${this.path}[${index}].${propertyPath}`,
            message,
          })
        )
      }
    })

    if (!lodashIsEmpty(errors)) {
      throw new yup.ValidationError(errors)
    }

    return true
  })
})

const ImagePathSchema = yup.string().nullable()

const MetadataObject = yup.object().shape({
  key: yup.string().required('knowledgeBase.errors.type.required'),
  value: yup.string()
    .required('knowledgeBase.errors.type.required')
    .test(
      'notValidJson',
      'knowledgeBase.errors.notValidJson',
      (value, testCtx) => {
        const { parent: { isJSON } } = testCtx
        if (isJSON && value) {
          try {
            JSON.parse(value)
            return true
          } catch {
            return false
          }
        }
        return true
      },
    ),
  isJSON: yup.boolean().required()
})

const PresentationOrTestSubFormSchema = yup.object().shape({
  difficulty: yup
    .number(),
  metadata: yup.array().of(MetadataObject)
    .uniqueProperty('key', 'knowledgeBase.errors.duplicateMetadataKey'),
})

const NameTranslationObject = yup.object().shape({
  languageCode: yup.string(),
  value: yup.string()
})

const DescriptionTranslationObject = yup.object().shape({
  languageCode: yup.string(),
  value: yup.string()
    .test(
      'descriptionLength',
      'knowledgeBase.errors.description.maxLength',
      (val) => !val || val?.length < 1024
    ),
})

const CreatePresentationOrTestSchema = yup.object().shape({
  type: yup
    .string()
    .oneOf([
      KnowledgeBaseContentType.PRESENTATION,
      KnowledgeBaseContentType.TEST,
      KnowledgeBaseContentType.SANDBOX_TEST
    ], 'knowledgeBase.errors.type.incorrect')
    .required('knowledgeBase.errors.type.required'),
  name: yup
    .string()
    .required('knowledgeBase.errors.name.required'),
  nameTranslations: yup.array().of(NameTranslationObject),
  descriptionTranslations: yup.array().of(DescriptionTranslationObject),
  description: yup
    .string()
    .required('knowledgeBase.errors.description.required')
    .test(
      'descriptionLength',
      'knowledgeBase.errors.description.maxLength',
      (val) => !!val && val?.length < 1024
    ),
  destination: yup
    .number()
    .nullable(true),
  knowledgeBaseFilesIds: yup
    .array().of(yup.number())
    .required('knowledgeBase.errors.knowledgeBaseFilesIds.required'),
  image: yup.lazy((value) => {
    if (value == null || typeof value === 'string') {
      return ImagePathSchema
    }
    return ImageSchema
  }),
  visibility: yup
    .string()
    .oneOf([
      KnowledgeBaseVisibilityType.Author,
      KnowledgeBaseVisibilityType.Company,
      KnowledgeBaseVisibilityType.UsersList
    ], 'knowledgeBase.errors.visibility.incorrect')
    .required('knowledgeBase.errors.visibility.required'),
  duration: yup
    .number()
    .integer('knowledgeBase.errors.duration.integer')
    .min(1, 'knowledgeBase.errors.duration.min')
    .max(1440, 'knowledgeBase.errors.duration.max')
    .required('knowledgeBase.errors.duration.required'),
  labelIds: yup.array().of(yup.number()),
  subForm: PresentationOrTestSubFormSchema,
})

const EditPresentationOrTestSchema = CreatePresentationOrTestSchema

const QuizSubFormSchema = yup.object().shape({
  quantityOfRandomQuestions: yup
    .number()
    .nullable(true)
    .test(
      'quantityOfRandomQuestions',
      'knowledgeBase.errors.quantityOfRandomQuestions',
      (value, testCtx) => {
        const { parent: { questions } } = testCtx
        if (value) {
          return value <= questions?.length
        }
        return true
      },
    ),
  questions: yup
    .array(),
  markWeight: yup
    .number()
    .integer('knowledgeBase.errors.markWeight.integer')
    .min(1, 'knowledgeBase.errors.markWeight.min')
    .max(3, 'knowledgeBase.errors.markWeight.max')
    .required('knowledgeBase.errors.markWeight.required'),
  randomQuestionOrder: yup
    .boolean()
    .required('knowledgeBase.errors.randomQuestionOrder.required'),
  showAnswers: yup
    .boolean()
    .required('knowledgeBase.errors.showAnswers.required'),
  canGoBack: yup
    .boolean()
    .required('knowledgeBase.errors.canGoBack.required'),
})

const CreateQuizSchema = yup.object().shape({
  type: yup
    .string()
    .oneOf([KnowledgeBaseContentType.QUIZ], 'knowledgeBase.errors.type.incorrect')
    .required('knowledgeBase.errors.type.required'),
  name: yup.string().required('knowledgeBase.errors.name.required'),
  nameTranslations: yup.array().of(NameTranslationObject),
  descriptionTranslations: yup.array().of(DescriptionTranslationObject),
  description: yup
    .string()
    .test(
      'descriptionLength',
      'knowledgeBase.errors.description.maxLength',
      (val) => (val ? val?.length < 1024 : true),
    ),
  destination: yup
    .number()
    .nullable(true),
  image: yup.lazy((value) => {
    if (value == null || typeof value === 'string') {
      return ImagePathSchema
    }
    return ImageSchema
  }),
  visibility: yup
    .string()
    .oneOf([
      KnowledgeBaseVisibilityType.Author,
      KnowledgeBaseVisibilityType.Company,
      KnowledgeBaseVisibilityType.UsersList
    ], 'knowledgeBase.errors.visibility.incorrect')
    .required('knowledgeBase.errors.visibility.required'),
  duration: yup
    .number()
    .integer('knowledgeBase.errors.duration.integer')
    .min(1, 'knowledgeBase.errors.duration.min')
    .max(1440, 'knowledgeBase.errors.duration.max')
    .nullable(true),
  labelIds: yup.array().of(yup.number()),
  subForm: QuizSubFormSchema,
})

const EditQuizSchema = CreateQuizSchema

const FileSubFormSchema = yup.object().shape({})

const CreateFileSchema = yup.object().shape({
  type: yup
    .string()
    .oneOf([KnowledgeBaseContentType.FILE], 'knowledgeBase.errors.type.incorrect')
    .required('knowledgeBase.errors.type.required'),
  name: yup.string().required('knowledgeBase.errors.name.required'),
  nameTranslations: yup.array().of(NameTranslationObject),
  descriptionTranslations: yup.array().of(DescriptionTranslationObject).nullable(true),
  description: yup
    .string()
    .test(
      'descriptionLength',
      'knowledgeBase.errors.description.maxLength',
      (val) => (val ? val?.length < 1024 : true),
    )
    .nullable(true),
  destination: yup
    .number()
    .nullable(true),
  image: yup.lazy((value) => {
    if (value == null || typeof value === 'string') {
      return ImagePathSchema
    }
    return ImageSchema
  }),
  visibility: yup
    .string()
    .oneOf([
      KnowledgeBaseVisibilityType.Author,
      KnowledgeBaseVisibilityType.Company,
      KnowledgeBaseVisibilityType.UsersList
    ], 'knowledgeBase.errors.visibility.incorrect')
    .required('knowledgeBase.errors.visibility.required'),
  duration: yup
    .number()
    .integer('knowledgeBase.errors.duration.integer')
    .min(1, 'knowledgeBase.errors.duration.min')
    .max(1440, 'knowledgeBase.errors.duration.max')
    .nullable(true),
  labelIds: yup.array().of(yup.number()),
  subForm: FileSubFormSchema,
})

const EditFileSchema = CreateFileSchema

const CreateKnowledgeBaseContentSchema = yup.lazy((value) => {
  const { type } = value
  if (type === KnowledgeBaseContentType.QUIZ) {
    return CreateQuizSchema
  }
  return CreatePresentationOrTestSchema
})

const EditKnowledgeBaseContentSchema = yup.lazy((value) => {
  const { type } = value
  if (type === KnowledgeBaseContentType.QUIZ) {
    return EditQuizSchema
  }
  if (type === KnowledgeBaseContentType.FILE) {
    return EditFileSchema
  }
  return EditPresentationOrTestSchema
})

export {
  CreateKnowledgeBaseContentSchema,
  EditKnowledgeBaseContentSchema,
}
